import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Child } from "../../api/child";
import { Class } from "../../api/class";
import { useGlobalStore } from "../../api/globalStores";
import { definitions } from "../../api/supabase/client";
import Avatar from "../../Common/Avatars/Avatar";
import ButtonPrimary from "../../Common/Buttons/ButtonPrimary";
import ReminderCard from "../../Common/Cards/ReminderCard";
import ScheduledLessonCard from "../../Common/Cards/ScheduledLessonCard";
import IconArrowLeftBlack from "../../Common/Icons/IconArrowLeftBlack";
import IconUserBlack from "../../Common/Icons/IconUserBlack";
import DropDownMenu from "../../Common/Menus/DropdownMenu/DropDownMenu";
import Progress from "../../Common/Progress/Progress";
import { ClassTiming, getClassTimings } from "../../Common/Utility/helpers";
import Modal from "../../Common/Utility/Modal";
import EngagementPanel from "../../Teacher/Components/EngagementPanelEditor/EngagementPanel";
import LangCompStatsBar from "../Components/LangCompStatsBar";
import { toJS } from "mobx";

export interface IStudentDashboard {}

export default observer(StudentDashboard);

type LanguageComponents = "Grammar" | "Pinyin" | "Radicals" | "Vocab";

const languageComponents: LanguageComponents[] = [
  "Grammar",
  "Pinyin",
  "Radicals",
  "Vocab",
];

type TimeSlotsMeta = {
  timeSlot: Partial<definitions["TimeSlot"]>;
  class: Class;
};

function StudentDashboard() {
  const globalStores = useGlobalStore();
  const navigate = useNavigate();
  const [openOnboardingQuiz, setOpenOnboardingQuiz] = useState(false);
  const [needOnboarding, setNeedOnboarding] = useState(false);
  const [showDropDown, setShowDropDown] = useState(false);
  const [curAnswer, setCurAnswer] = useState<
    "A" | "B" | "C" | "D" | undefined
  >();
  const [answeringMode, setAnsweringMode] = useState<
    "ANSWERING" | "SHOW_ANSWER"
  >("ANSWERING");
  const [timeSlots, setTimeSlots] = useState<TimeSlotsMeta[]>([]);

  const continueOnboardingQuiz = useCallback(async () => {
    const newNeedOnboarding =
      await globalStores.childStore.continueOnboardingQuiz();
    if (!newNeedOnboarding) {
      setOpenOnboardingQuiz(false);
    }
    setNeedOnboarding(newNeedOnboarding);
  }, [globalStores.childStore]);

  useEffect(() => {
    const run = async () => {
      const classes = (
        await Promise.all(
          globalStores.childStore.classes.map(async (classDetails) => {
            if (!classDetails.id) return;
            const newClass = new Class();
            await newClass.init(classDetails.id);
            return newClass;
          })
        )
      ).filter((myClass) => myClass !== undefined);

      const newTimeSlots = classes.flatMap((newClass) => {
        return (
          newClass?.timeSlots.map((timeSlot) => ({
            timeSlot,
            class: newClass,
          })) ?? []
        );
      });

      setTimeSlots(newTimeSlots);
    };

    run();
  }, [globalStores.childStore.classes]);

  useEffect(() => {
    const run = async () => {
      if (!globalStores.childStore.childAccount.id) {
        navigate("/parent/child_selection");
      }
      await continueOnboardingQuiz();
    };

    run();
  }, [navigate, globalStores.childStore, continueOnboardingQuiz]);

  const [paymentDetails, setPaymentDetails] = useState<
    definitions["PaymentStatus"][]
  >([]);

  useEffect(() => {
    const run = async () => {
      const newPaymentDetails =
        await globalStores.parentStore.getPaymentDetails();
      setPaymentDetails(newPaymentDetails);
    };

    run();
  }, []);

  //fetch stats data
  useEffect(() => {
    globalStores.childStore.fetchChildLearningProfileData();
  }, []);

  const handleNext = async () => {
    if (answeringMode === "ANSWERING") {
      if (curAnswer === undefined) {
        throw new Error("No answer selected! Please select an answer first!");
      }
      await globalStores.curEPanel.answerEngagementPanel(curAnswer);
      setAnsweringMode("SHOW_ANSWER");
    } else if (answeringMode === "SHOW_ANSWER") {
      setAnsweringMode("ANSWERING");
      setCurAnswer(undefined);
      await continueOnboardingQuiz();
    }
  };

  const payingChildIDs = paymentDetails.map(
    (paymentDetail) => paymentDetail.account
  );

  return (
    <>
      <Modal
        open={openOnboardingQuiz}
        onClose={() => {
          setOpenOnboardingQuiz(false);
        }}
      >
        <div className="bg-brij-White flex p-4 flex-col items-center gap-5 w-[400px] rounded-2xl shadow-xl h-[95dvh]">
          <Progress
            currentStep={globalStores.childStore.curOnboardingStep + 1}
            totalSteps={globalStores.childStore.totalOnboardingSteps}
          />
          {/* <div className="flex-1 w-full items-center justify-center flex overflow-scroll"> */}
          <div className="flex-1 overflow-clip">
            <EngagementPanel
              mode={answeringMode}
              onAnswerSelected={(newAns) => {
                setCurAnswer(newAns);
              }}
              curSelectedAnswer={curAnswer}
              isOnboarding
            />
          </div>
          <ButtonPrimary
            onClick={() => {
              toast.promise(handleNext(), {
                success:
                  answeringMode === "ANSWERING"
                    ? "Answer Submitted"
                    : "Next Question Loaded",
                loading:
                  answeringMode === "ANSWERING"
                    ? "Submitting Answer"
                    : "Loading Next Question",
                error: (e) => {
                  return e.message;
                },
              });
            }}
          >
            {answeringMode === "ANSWERING" ? "Submit" : "Next Question"}
          </ButtonPrimary>
        </div>
      </Modal>
      <div className="bg-brij-Default w-full h-full flex flex-col relative bg-hill_children_white bg-contain bg-no-repeat bg-bottom backdrop-opacity-50 overflow-y-scroll">
        {/* HEADER */}
        <div className="flex gap-6 p-8 pb-3 items-center w-full z-20">
          <div
            className="w-16 h-16 cursor-pointer"
            onClick={() => {
              setShowDropDown(!showDropDown);
            }}
          >
            <Avatar
              icon={Child.getIcon(globalStores.childStore)}
              active={false}
              large
            />
            <div className={showDropDown ? "absolute left-11 z-30" : "hidden"}>
              <DropDownMenu
                menuItems={[
                  {
                    text: "All Children",
                    icon: <IconUserBlack />,
                    onClick: () => {
                      navigate("/parent/child_selection");
                    },
                  },
                  {
                    text: "Sign Out",
                    icon: <IconArrowLeftBlack />,
                    onClick: () => {
                      toast.promise(
                        (async () => {
                          navigate("/parent/child_selection");
                        })(),
                        {
                          success: "Sign out Success!",
                          error: (e) => e.message,
                          loading: "Signing out...",
                        }
                      );
                    },
                  },
                ]}
              />
            </div>
          </div>
          <p className="font-medium text-xlarge">
            Welcome back, {globalStores.childStore.userDetail.firstName}
          </p>
        </div>
        {/* DASHBOARD */}
        <div className="w-full flex flex-col md:flex-row items-center justify-center gap-8  pb-12">
          <div className="w-[40%] h-full flex flex-col gap-2">
            <p className="text-heading3 font-semibold">Stats</p>
            {languageComponents.map((langComp) => {
              const langCompObj =
                globalStores.childStore.childLearningProfile.languageComponents;
              const answered = langCompObj?.[`brij${langComp}Answered`];
              const correct = langCompObj?.[`brij${langComp}Correct`];
              const proportion =
                !answered || answered < 10 || !correct
                  ? -1
                  : correct / answered;
              return (
                <LangCompStatsBar key={langComp} proportion={proportion} title={langComp} />
              );
            })}
          </div>
          <div className="flex flex-col h-full gap-2 w-full md:w-[290px] px-6 md:px-0">
            {/* <p className="text-heading3 font-semibold">Notifications</p>
            {needOnboarding ? (
              <ReminderCard
                header={"NOTIFICATION"}
                content={"Have you completed your onboarding quiz?"}
                buttonText={"Start Quiz"}
                onClick={() => setOpenOnboardingQuiz(true)}
              />
            ) : (
              <div className="w-full text-center text-brij-DarkGrey font-poppins">
                No Notifications
              </div>
            )} */}
            <MySchedule timeSlots={timeSlots} payingChildIDs={payingChildIDs} />
          </div>
        </div>
      </div>
    </>
  );
}

const getDayAndTimeOfWeek = (dateTime: Date) => {
  const options = {
    weekday: "long",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  } as const;
  return new Intl.DateTimeFormat("en-US", options).format(dateTime);
};

function MySchedule({
  timeSlots,
  payingChildIDs,
}: {
  timeSlots: TimeSlotsMeta[];
  payingChildIDs: (number | null)[];
}) {
  const globalStores = useGlobalStore();

  const classTimings = timeSlots.flatMap((slot) => {
    const endDate = slot.class.class.endDate;
    if (endDate && endDate <= new Date()) {
      return [];
    }
    return getClassTimings(slot.class, slot.timeSlot);
  });

  const classTimingsByImportance = classTimings.sort((timingA, timingB) => {
    const now = new Date().getTime();

    const getClassStatus = (timing: ClassTiming) => {
      if (timing.start.getTime() <= now && timing.end.getTime() >= now) {
        return 0; // ongoing
      } else if (timing.start.getTime() > now) {
        return 1; // upcoming
      } else {
        return 2; // past
      }
    };

    const statusA = getClassStatus(timingA);
    const statusB = getClassStatus(timingB);

    if (statusA !== statusB) {
      return statusA - statusB;
    }

    const distanceA = Math.abs(now - timingA.start.getTime());
    const distanceB = Math.abs(now - timingB.start.getTime());
    return distanceA - distanceB;
  });

  const relevantClassTimings = useMemo(() => {
    const timings: ClassTiming[] = [];
    classTimingsByImportance.forEach((timing) => {
      if (
        timings.some(
          (existingTiming) => existingTiming.classID === timing.classID
        )
      )
        return;
      timings.push(timing);
    });
    return timings;
  }, [classTimingsByImportance]);

  const classIntervalStrings: Record<string, string> = useMemo(() => {
    const uniqueWeekTimes: Record<string, Set<string>> = {};
    classTimingsByImportance.forEach((timing) => {
      const weekTime = getDayAndTimeOfWeek(timing.start);
      uniqueWeekTimes[timing.classID] ??= new Set();
      uniqueWeekTimes[timing.classID].add(weekTime);
    });

    //Returns in the format {[classId]:classIntervalString}
    return Object.fromEntries(
      Object.entries(uniqueWeekTimes).map(([classId, uniqueWeekTimes]) => {
        return [classId, Array.from(uniqueWeekTimes).join(", ")];
      })
    );
  }, [relevantClassTimings]);

  return (
    <>
      <p className="text-heading3 font-semibold">My Schedule</p>
      {relevantClassTimings.length === 0 ? (
        <div className="w-full text-center text-brij-DarkGrey font-poppins py-4">
          No classes yet
        </div>
      ) : (
        relevantClassTimings.map((classTiming) => {
          return (
            <ScheduledLessonCard
              classIntervalString={classIntervalStrings[classTiming.classID]}
              classTiming={classTiming}
              key={classTiming.start.toISOString()}
              isPaid={payingChildIDs.includes(
                globalStores.childStore.childAccount.id ?? null
              )}
              duration={"45min"}
            />
          );
        })
      )}
    </>
  );
}
